import React, { useState, useEffect } from 'react'
import { Room, Player, nullRoom, nullPlayer } from '../Types'
import { useParams, Link, useHistory } from 'react-router-dom'
import { ListItem, List, Stack, Background, LobbyButton, LobbyContainer, LobbyCard, LobbyTitle, LobbyText, GameIdDisplay, PlayersList, PlayersListItem } from './styles'
import LoadingIndicator from '../components/LoadingIndicator'
import {
    RemovePlayer,
    setReadyStatus,
    numberReady,
    getUsernames,
    startGame,
    numRealPlayers,
} from './utils'
import { useSSE } from '../hooks/useSSE'

import isObjEqual from 'fast-deep-equal'

import clipboardCopy from 'clipboard-copy';
import { ToastContainer, toast} from 'react-toastify'

import { MaterialCheck, MaterialCross } from '../Images/images'
import 'react-toastify/dist/ReactToastify.css'
import { useHistoryState } from '../StateContext'

const Lobby = () => {
    let { gameId } = useParams()
    const [ready, setReady] = useState<boolean>(false)
    const [players, setPlayers] = useState<Player[]>([])
    const [readyToStart, setReadyToStart] = useState<boolean>(false)
    const [usernameList, setUsernameList] = useState<string[]>([])
    const [sendToGame, setSendToGame] = useState<boolean>(false)
    let history = useHistory()

    const {state, setState} = useHistoryState()

    let showingToast = false

    // Use SSE hook for real-time room updates
    const { room, isConnected, error } = useSSE({
        gameId,
        playerId: state.username,
        onUpdate: (r) => {
            if (!isObjEqual(r, nullRoom)) {
                // Always update players list (not just when count changes)
                setPlayers(r.players)
                setUsernameList(getUsernames(r.players))

                // Check if ready to start
                const numReady = numberReady(r)
                const numReal = numRealPlayers(r.players)
                console.log('[Lobby] Ready check:', {
                    numReady,
                    numReal,
                    players: r.players.map(p => ({ id: p.playerId, ready: p.ready })),
                    meetsCondition: numReady === numReal && numReady >= 2
                })
                if (numReady === numReal && numReady >= 2) {
                  console.log('set the readyToStart to true')
                    setReadyToStart(true)
                } else {
                    setReadyToStart(false)
                }

                // Check if game has started
                if (r.hasStarted === true) {
                    setSendToGame(true)
                }
            }
        },
        onError: (err) => {
            console.error('SSE Error in Lobby:', err)
        }
    })
    let username = state.username
    window.onbeforeunload = () => {
        setReadyStatus(username, gameId, false)
    }

    // Show loading state while SSE is connecting
    if (!isConnected) {
        return <LoadingIndicator message="Connecting to lobby" />
    }

    // Check if game has started and navigate away FIRST
    if (sendToGame) {
        history.push(`/start/${gameId}/`)
        return null
    }

    // Once connected, check if user is in the room
    if (usernameList.indexOf(username) < 0 || username === "") {
        return (
            <Background>
                <Stack>
                    <div>
                        If joining the game, give it a moment. CS is hard. :D
                    </div>
                    <div>
                        If not, it seems like you were kicked out of the game.
                        Click here to go <Link onClick={()=>{}} to="/">back.</Link>
                    </div>
                </Stack>
            </Background>
        )
    }

    if (gameId == null || gameId === '') {
        return null
    }

    console.log(`username = room.host.id: ${username === room.host.playerId}, username: ${username}, room host: ${room.host.playerId}`)

    return(
        <Background>
            <LobbyContainer>
                <LobbyCard>
                    <LobbyTitle>
                        Welcome, {username}!
                    </LobbyTitle>

                    <LobbyText style={{ fontSize: "14px", opacity: 0.9 }}>
                        Share this Game ID with your friends to invite them:
                    </LobbyText>

                    <GameIdDisplay
                        onClick={() => {
                            if (!showingToast) {
                                showingToast = true
                                clipboardCopy(gameId)
                                .then(() => {
                                    toast.info("Game ID copied to clipboard!",
                                    {
                                        position: "top-center",
                                        autoClose: 2000,
                                        hideProgressBar: false,
                                        closeOnClick: true,
                                        pauseOnHover: true,
                                        draggable: false,
                                        className: "custom-turn-toast",
                                        onClose: () => {showingToast = false},
                                    })
                                })
                            }
                        }}
                    >
                        {gameId}
                    </GameIdDisplay>

                    <LobbyText style={{ fontSize: "13px", opacity: 0.7 }}>
                        Click the Game ID to copy it • Don't close this window!
                    </LobbyText>

                    <div style={{ width: "100%", marginTop: "8px" }}>
                        <LobbyText style={{ marginBottom: "16px", fontWeight: 600, fontSize: "18px" }}>
                            Players in Lobby
                        </LobbyText>
                        <PlayersList>
                            {players.map((player, index) => {
                                // Don't show null players.
                                if (isObjEqual(player, nullPlayer)) {
                                    return null
                                }
                                return (
                                    <PlayersListItem key={player.playerId || index}>
                                        <span style={{ display: "flex", alignItems: "center", gap: "12px", fontSize: "16px" }}>
                                            <span style={{ opacity: player.ready ? 1 : 0.5 }}>
                                                {player.ready ? <MaterialCheck/> : <MaterialCross/>}
                                            </span>
                                            <span style={{ fontWeight: 500 }}>
                                                {player.playerId}
                                                {player.playerId === room.host.playerId &&
                                                    <span style={{
                                                        marginLeft: "8px",
                                                        fontSize: "12px",
                                                        color: "#fbbf24",
                                                        fontWeight: 600
                                                    }}>
                                                        HOST
                                                    </span>
                                                }
                                            </span>
                                        </span>
                                        {username === room.host.playerId && username !== player.playerId ?
                                            <LobbyButton
                                                backgroundColor="linear-gradient(135deg, #ef4444 0%, #dc2626 100%)"
                                                width="80px"
                                                height={35}
                                                onClick={() => {
                                                    RemovePlayer(player.playerId, gameId)
                                                }}
                                            >
                                                Kick
                                            </LobbyButton>
                                            : <div style={{ width: "80px" }}></div>
                                        }
                                    </PlayersListItem>
                                )
                            })}
                        </PlayersList>
                    </div>

                    <div style={{
                        width: "100%",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: "12px",
                        marginTop: "8px"
                    }}>
                        <LobbyButton
                            width="400px"
                            disabled={!(username === room.host.playerId && readyToStart)}
                            onClick={()=> {
                                startGame(gameId)
                            }}
                        >
                            {username === room.host.playerId
                                ? (readyToStart ? "Start Game" : "Waiting for players to ready up...")
                                : "Waiting for host to start game..."
                            }
                        </LobbyButton>

                        <LobbyButton
                            width="400px"
                            backgroundColor={ready
                                ? "linear-gradient(135deg, #10b981 0%, #059669 100%)"
                                : "linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)"
                            }
                            onClick={() => {
                                setReady(!ready)
                                setReadyStatus(username, gameId, !ready)
                            }}
                        >
                            {ready ? "Ready ✓" : "Click to Ready Up"}
                        </LobbyButton>

                        <LobbyButton
                            width="400px"
                            backgroundColor="linear-gradient(135deg, #ef4444 0%, #dc2626 100%)"
                            onClick={()=>{
                                RemovePlayer(username, gameId).then(()=>{
                                    setState({username: state.username, gameId: ""})
                                })
                                history.push(`/`)
                            }}
                        >
                            Leave Lobby
                        </LobbyButton>
                    </div>
                </LobbyCard>
            </LobbyContainer>
            <ToastContainer/>
        </Background>
    )
} 

export default Lobby